import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { roundTo2Decimals } from '@principle-theorem/accounting';
import {
  CurrentScopeFacade,
  GlobalStoreService,
} from '@principle-theorem/ng-principle-shared';
import {
  BasicDialogService,
  TrackByFunctions,
  TypedFormControl,
  TypedFormGroup,
  formControlChanges$,
} from '@principle-theorem/ng-shared';
import {
  AccountCredit,
  TransactionAllocation,
} from '@principle-theorem/principle-core';
import { PatientPermissions } from '@principle-theorem/principle-core/features';
import {
  IStaffer,
  ITreatmentCategory,
  type IAccountCredit,
  type IAccountCreditExtendedData,
  type IAppointment,
  type IInvoice,
  type IPayWithCreditResults,
  type IPractice,
  type ITransaction,
} from '@principle-theorem/principle-core/interfaces';
import {
  INamedDocument,
  isSameRef,
  type AtLeast,
  type DocumentReference,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, min, sum } from 'lodash';
import { combineLatest, of, type Observable } from 'rxjs';
import { map, startWith, switchMap } from 'rxjs/operators';
import {
  TransactionPracticeOptionMethods,
  TransactionPracticeOptions,
} from '../transaction-components/transaction-practice-options';
import { bindLimitToAmountControl } from '../transaction-amount-limit';
import {
  AccountCreditTransactionBuilder,
  type IAccountCreditTransactionBuildResult,
} from './account-credit-transaction-builder';

/**
 * One reservation on a credit, ready to render. `swatchColour` is only set for
 * a treatment category, which is the sole reservation that carries a colour.
 */
export interface IReservedChip {
  label: string;
  tone: 'neutral' | 'flagged';
  swatchColour?: string;
}
export interface IAccountCreditTransactionDialogData {
  transaction: AtLeast<ITransaction, 'to' | 'from'>;
  credits: WithRef<IAccountCredit>[];
  invoice: WithRef<IInvoice>;
  /**
   * Credits reserved for this appointment are offered first; credits reserved
   * for any other appointment require an explicit confirmation to consume.
   */
  appointmentRef?: DocumentReference<IAppointment>;
}

export interface IAccountCreditTransactionReturnData
  extends IPayWithCreditResults {
  transaction: ITransaction<IAccountCreditExtendedData>;
}

interface IAccountCreditFormData {
  amount: number;
  accountCredits: WithRef<IAccountCredit>[];
  practiceRef: DocumentReference<IPractice>;
  attributedTo?: DocumentReference<IStaffer>;
}

@Component({
  selector: 'pr-account-credit-transaction',
  templateUrl: './account-credit-transaction.component.html',
  styleUrls: ['./account-credit-transaction.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AccountCreditTransactionComponent {
  private _protectedCredits: WithRef<IAccountCredit>[];
  trackByAccountCredit = TrackByFunctions.ref<IAccountCredit>();
  availableCredits: WithRef<IAccountCredit>[];
  isDisabled$: Observable<boolean>;
  available$: Observable<number>;
  limit$: Observable<number>;
  isLimitedByInvoiceRemaining$: Observable<boolean>;
  form = new TypedFormGroup<IAccountCreditFormData>({
    amount: new TypedFormControl<number>(0),
    accountCredits: new TypedFormControl<WithRef<IAccountCredit>[]>([]),
    practiceRef: new TypedFormControl<DocumentReference<IPractice>>(undefined),
    attributedTo: new TypedFormControl<DocumentReference<IStaffer> | undefined>(
      undefined
    ),
  });
  automationListMessage = `Account credits selected below will only be "used" until the amount above is reached.`;
  practiceOptions: TransactionPracticeOptions;
  accountAdminPermission = PatientPermissions.AccountInvoiceAdmin;
  staff: INamedDocument<IStaffer>[];
  showAttributedTo: boolean;

  constructor(
    private _dialogRef: MatDialogRef<
      AccountCreditTransactionComponent,
      IAccountCreditTransactionReturnData
    >,
    @Inject(MAT_DIALOG_DATA) public data: IAccountCreditTransactionDialogData,
    private _currentScope: CurrentScopeFacade,
    private _globalStore: GlobalStoreService,
    private _basicDialog: BasicDialogService,
    private _destroyRef: DestroyRef
  ) {
    const positiveBalanceCredits = this.data.credits.filter(
      (credit) => AccountCredit.remaining(credit) > 0
    );
    const { matching, unreserved, otherAppointment } =
      AccountCredit.classifyForAppointment(
        positiveBalanceCredits,
        this.data.appointmentRef
      );

    const safeCredits = [...matching, ...unreserved];
    this._protectedCredits = otherAppointment;
    this.availableCredits = [...safeCredits, ...otherAppointment];

    this.form.patchValue({
      amount: sum(safeCredits.map((credit) => AccountCredit.remaining(credit))),
      accountCredits: safeCredits,
    });

    this.available$ = formControlChanges$(
      this.form.controls.accountCredits
    ).pipe(
      map((credits) =>
        credits
          ? sum(credits.map((credit) => AccountCredit.remaining(credit)))
          : 0
      )
    );

    this.isDisabled$ = combineLatest([
      this.available$.pipe(map((available) => available > 0)),
      this.form.valueChanges.pipe(
        map(() => this.form.valid),
        startWith(this.form.valid)
      ),
    ]).pipe(map(([hasCredits, isValid]) => !hasCredits || !isValid));

    this.limit$ = combineLatest([
      this.available$,
      formControlChanges$(this.form.controls.attributedTo),
    ]).pipe(
      switchMap(async ([available, attributedTo]) => {
        const maxAllocatableForPractitioner =
          await TransactionAllocation.getMaxAllocatableForPractitioner(
            this.data.invoice,
            attributedTo ?? undefined
          );

        return roundTo2Decimals(
          min([
            available,
            this.data.transaction.amount,
            maxAllocatableForPractitioner,
          ]) || 0
        );
      })
    );

    this.isLimitedByInvoiceRemaining$ = combineLatest([
      this.limit$,
      this.available$,
    ]).pipe(map(([limit, available]) => limit < available));

    bindLimitToAmountControl(
      this.limit$,
      this.form.controls.amount,
      this._destroyRef,
      { required: false }
    );

    this.practiceOptions = new TransactionPracticeOptions(
      this._currentScope.currentPractice$,
      TransactionPracticeOptionMethods.toInvoicePracticeOption(
        this.data.invoice
      ),
      true
    );
    this.practiceOptions.initialValue$
      .pipe(takeUntilDestroyed())
      .subscribe((practice) =>
        this.form.controls.practiceRef.setValue(practice.ref)
      );

    this.practiceOptions.options$
      .pipe(takeUntilDestroyed())
      .subscribe((practices) => {
        if (practices.length > 1) {
          this.form.controls.practiceRef.setValidators(Validators.required);
          this.form.controls.practiceRef.updateValueAndValidity();
        }
      });

    const { staff, showAttributedTo } =
      TransactionAllocation.getAttributedToConfig(this.data.invoice);
    this.staff = staff;
    this.showAttributedTo = showAttributedTo;
  }

  /**
   * Flagged rather than neutral: a credit held for a different appointment is
   * the one the dialog leaves unticked and guards behind a confirmation, so it
   * has to read as an exception before staff select it.
   */
  private static _appointmentChip(
    credit: WithRef<IAccountCredit>,
    dialogAppointmentRef?: DocumentReference<IAppointment>
  ): IReservedChip | undefined {
    const reservedAppointmentRef = credit.reservedFor.appointmentRef;
    if (!reservedAppointmentRef) {
      return;
    }
    const isThisAppointment =
      !!dialogAppointmentRef &&
      isSameRef(reservedAppointmentRef, dialogAppointmentRef);
    return isThisAppointment
      ? { label: 'This appointment', tone: 'neutral' }
      : { label: 'Another appointment', tone: 'flagged' };
  }

  private static _categoryChip(
    treatmentCategory?: WithRef<ITreatmentCategory>
  ): IReservedChip | undefined {
    if (!treatmentCategory) {
      return;
    }
    return {
      label: treatmentCategory.name,
      tone: 'neutral',
      swatchColour: treatmentCategory.colour.value,
    };
  }

  private static _practitionerChip(
    credit: WithRef<IAccountCredit>
  ): IReservedChip | undefined {
    const practitioner = credit.reservedFor.practitioner;
    return practitioner
      ? { label: practitioner.name, tone: 'neutral' }
      : undefined;
  }

  creditRemaining(credit: WithRef<IAccountCredit>): number {
    return AccountCredit.remaining(credit);
  }

  resolveTreatmentCategory$(
    credit: WithRef<IAccountCredit>
  ): Observable<WithRef<ITreatmentCategory> | undefined> {
    if (!credit.reservedFor.treatmentCategory) {
      return of(undefined);
    }
    return this._globalStore.getTreatmentCategory$(
      credit.reservedFor.treatmentCategory
    );
  }

  /**
   * The reservations on a credit, one chip each. The three constraints are
   * independent — a credit can be held for an appointment, a practitioner, a
   * treatment category, or any combination — so each is resolved on its own
   * and absent ones simply produce no chip. An empty list means the credit
   * carries no reservation at all, and the row says nothing about one.
   */
  resolveReservedChips$(
    credit: WithRef<IAccountCredit>
  ): Observable<IReservedChip[]> {
    return this.resolveTreatmentCategory$(credit).pipe(
      map((treatmentCategory) =>
        compact([
          AccountCreditTransactionComponent._appointmentChip(
            credit,
            this.data.appointmentRef
          ),
          AccountCreditTransactionComponent._categoryChip(treatmentCategory),
          AccountCreditTransactionComponent._practitionerChip(credit),
        ])
      )
    );
  }

  async submit(): Promise<void> {
    if (!this.form.valid) {
      return;
    }

    const formData = this.form.value;
    const orderedCredits = [...formData.accountCredits].sort(
      (creditA, creditB) => {
        const indexA = this.availableCredits.findIndex((availableCredit) =>
          isSameRef(availableCredit, creditA)
        );
        const indexB = this.availableCredits.findIndex((availableCredit) =>
          isSameRef(availableCredit, creditB)
        );
        return indexA - indexB;
      }
    );
    const result =
      AccountCreditTransactionBuilder.buildAccountCreditTransaction({
        baseTransaction: { ...this.data.transaction, amount: formData.amount },
        credits: orderedCredits,
        amount: formData.amount,
        practiceRef: formData.practiceRef,
        attributedTo: formData.attributedTo,
      });

    const confirmed = await this._confirmProtectedCredits(result);
    if (!confirmed) {
      return;
    }

    this._dialogRef.close(result);
  }

  compareFn(
    accountCreditA: WithRef<IAccountCredit>,
    accountCreditB: WithRef<IAccountCredit>
  ): boolean {
    return accountCreditA && accountCreditB
      ? isSameRef(accountCreditA, accountCreditB)
      : false;
  }

  private async _confirmProtectedCredits(
    result: IAccountCreditTransactionBuildResult
  ): Promise<boolean> {
    const consumed = this._protectedCredits.filter((protectedCredit) =>
      result.alteredCredits.some((alteredCredit) =>
        isSameRef(alteredCredit.accountCredit, protectedCredit)
      )
    );
    if (!consumed.length) {
      return true;
    }

    const descriptions = consumed
      .map((credit) => credit.description)
      .join(', ');
    const confirmed = await this._basicDialog.confirm({
      title: 'Use booking deposit from another appointment?',
      prompt: `The following Account Credits are reserved for other appointments: ${descriptions}. Using them here will make them unavailable for those appointments. Continue?`,
    });
    return confirmed === true;
  }
}
